import React from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import cn from 'classnames';
import { first } from 'lodash';

import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import { feCleanCheckoutData } from 'actions/ui.actions';
import { LT_CHECKOUT } from 'bl/loggers.bl';
import CheckoutFooter from 'components/checkout/CheckoutFooter.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import CurrencySign from 'components/price/CurrencySign.component';
import PUSHER from 'config/pusher.singleton';
import CHECKOUT_OPTION from 'constants/checkoutOption.const';
import DELAY from 'constants/delay.const';
import LINK from 'constants/link.const';
import { useTranslation, withGilt, withTranslation } from 'providers/GILT.provider';
import { PUSHER_EVENT_CHECKOUT_SCAN } from 'providers/Pusher.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectCheckoutData } from 'selectors/checkout.selectors';
import { selectOldFlagsChargeCardOnFile } from 'selectors/oldFlags.selectors';
import { selectRsCheckoutFetchAllDeposits, selectRsCheckoutFetchOneDeposit, selectRsFetchDepositClientList } from 'selectors/request.selectors';
import getAny from 'utils/accessors/getAny.util';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { fromPropsToIsWeek, fromPropsToMatchedParams } from 'utils/converters/props/path.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import delayed from 'utils/functions/delayed.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parsfixed from 'utils/values/parsfixed.util';

import { getAppointmentStartTimeList, getCategories, getClientsList } from 'booking/actions';
import {
  addClient,
  addClientDeposits,
  addClientSearchDeposits,
  closeCheckoutSession,
  fetchAllDepositClientsList,
  fetchClientDepositList,
  fetchDeposit,
  fetchNextAppointmentDetails,
  fetchProductList,
  removeClient,
  setCheckoutPayButtons,
  setCheckoutTotal,
  startCheckoutSession,
  updateCheckoutData,
  updateDepositFromCheckout,
} from 'checkout/actions';
import AlertDialog from 'checkout/components/AlertDialog';
import CheckoutClient from 'checkout/components/CheckoutClient';
import CheckoutDepositSearch from 'checkout/components/CheckoutDepositSearch';
import { CheckoutTaxes } from 'checkout/components/CheckoutTaxes';
import CheckoutTipsContainer from 'checkout/components/CheckoutTips';
import CheckoutTitle from 'checkout/components/CheckoutTitle';
import ClientsDepositTable from 'checkout/components/ClientsDepositTableLoader.component';
import SelectPaymentOptionModal from 'checkout/components/SelectPaymentOptionModal.component';
import CheckoutVoucherV2 from 'checkout/components/voucher/CheckoutVoucher.component';
import WalkInInstructions from 'checkout/components/WalkInInstructions.component';
import { CHECKOUT_ADD_PRODUCT_FROM_SCAN } from 'checkout/constants';
import { calculateCheckoutTaxesAndFees } from 'checkout/reducer';

import 'styles/checkout/checkout.scss';
import 'styles/checkout/modal.scss';

const CTAG = 'Checkout';
const L = logger([CTAG, LT_CHECKOUT]);

const DEFAULT_OTHER_AMOUNT = '0';

const CheckoutHeader = ({ onGoBack }) => {
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  return (
    <div data-bem="CheckoutHeader" className="header">
      <div className="header-top">
        <div data-bem="CheckoutHeader__button" role="button" tabIndex="0" onClick={onGoBack}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link data-bem="CheckoutHeader__link" className="menu-icon" to={`/salon/${salonId}/day/today`}>
            <span className="icon ico-back" />
            {t('checkout.goBackButton')}
          </Link>
        </div>
        <h3>{t('checkout.title')}</h3>
      </div>
    </div>
  );
};

class Checkout extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errorMessageModal: false,
      requestedDeposit: false,
      depositLoadedFromServer: false,
      dayView: true,
      defaultPayMethod: '',
      tipsRow: false,
      searchDepositAdded: false,
    };
  }

  UNSAFE_componentWillMount() {
    const FTAG = 'componentWillMount()';

    const { getClientsList, clients, fetchProductList, fetchStylistsBySalon, categories, getCategories, salonId, isSalonInUS } = this.props;

    L.unignore(FTAG, 'fetchProductList:', fetchProductList(salonId, isSalonInUS));

    if (asArray(clients).length === 0) {
      L.unignore(FTAG, 'fetchStylistsBySalon:', fetchStylistsBySalon(salonId));
      L.unignore(FTAG, 'getClientsList:', getClientsList(salonId));
    }

    if (asArray(categories).length === 0) {
      L.unignore(FTAG, 'getCategories:', getCategories(salonId));
    }

    if (fromPropsToIsWeek(this.props)) {
      this.setState({ dayView: false });
    }
  }

  componentDidMount() {
    const { salonId, startCheckoutSession, stylists } = this.props;

    this.subscribe();

    const defaultStylist = first(stylists.filter(({ id, status }) => status === 'active' && id));
    const stylistId = getAny(0, 'id', defaultStylist);

    L.unignore('componentDidMount()', 'startCheckoutSession:', startCheckoutSession(salonId, stylistId));
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({ errorMessageModal: fromAnyToBool(nextProps.errorMessage) });
  }

  componentWillUnmount() {
    const { cleanCheckoutData, salonId, closeCheckoutSession } = this.props;

    L.unignore('componentWillUnmount()', 'cleanCheckoutData:', cleanCheckoutData());
    L.unignore('componentWillUnmount()', 'closeCheckoutSession:', closeCheckoutSession(salonId));

    this.unsubscribe();
  }

  onClosePaymentModal = (defaultPayMethod) => void this.setState({ defaultPayMethod });

  setUpDepositFromBackEnd = () => {
    const FTAG = 'setUpDepositFromBackEnd()';

    const { initialDeposit, clients: clientsFromProps, addClientDeposits, fetchClientDepositList, clientsDepositsSelected, fetchDeposit, selectedAppointment, appointments, salonId } = this.props;
    const { requestedDeposit } = this.state;

    if (selectedAppointment?.id && !requestedDeposit) {
      delayed(DELAY.instant, () =>
        // FIXME: this should note be here called by the render phase
        this.setState(
          { requestedDeposit: true },
          // FIXME: and this is just what it should be set alongside requestedDeposit: true
          () => {
            if (!selectedAppointment || appointments.length >= 2) return;

            const clientId = selectedAppointment?.client_id;
            if (!clientId) return;

            const aId = selectedAppointment?.id ?? 0;
            const sId = fromAnyToString(aId);

            L.unignore(FTAG, 'fetchDeposit:', fetchDeposit(salonId, aId));
            L.unignore(FTAG, 'fetchClientDepositList:', fetchClientDepositList(salonId, clientId, sId));
          },
        ),
      );
    }

    const clientId = getAnyOrVoid('response.clientId', initialDeposit);
    const amount = getAnyOrVoid('response.amount', initialDeposit);

    if (!requestedDeposit || !clientId || !amount) return;

    const depositsForClient = asArray(clientsDepositsSelected).filter(({ id }) => id === clientId);
    if (depositsForClient.length) return;

    const clients = asArray(clientsFromProps).filter(({ id }) => id === clientId);
    if (!clients.length) return;

    addClientDeposits(first(clients), Number.parseFloat(amount)); // TODO: replace Number.parseFloat with util function
    this.setState({ depositLoadedFromServer: true });
  };

  subscribe = () =>
    void PUSHER.redux.subscribe({
      channelName: `${this.props.salonId}_channel`,
      eventName: PUSHER_EVENT_CHECKOUT_SCAN,
      actionType: CHECKOUT_ADD_PRODUCT_FROM_SCAN,
    });

  unsubscribe = () =>
    void PUSHER.redux.unsubscribe({
      channelName: `${this.props.salonId}_channel`,
      eventName: PUSHER_EVENT_CHECKOUT_SCAN,
      actionType: CHECKOUT_ADD_PRODUCT_FROM_SCAN,
    });

  addClient = (client) => {
    const { allProceduresForTheDay, addClient } = this.props;
    const storeClient = {
      id: client.id,
      clientId: client.id,
      appointmentId: null,
      services: [],
      products: [],
      nextAppointment: null,
    };

    addClient(storeClient, allProceduresForTheDay);
  };

  addClientDeposits = (client) => {
    this.props.addClientSearchDeposits(client);
    this.setState({ searchDepositAdded: true });
  };

  handleComplete = (options) => {
    const { updateCheckoutData, salonId, chargeCardOnFile } = this.props;

    const isClientCard = options?.checkoutOption !== CHECKOUT_OPTION.terminal;
    updateCheckoutData(isClientCard, options?.cash, options?.other, chargeCardOnFile);

    const { dayView } = this.state;
    const { stylistId } = fromPropsToMatchedParams(this.props);
    const isWeek = fromPropsToIsWeek(this.props);

    navFor(CTAG, 'handleComplete()', { dayView, stylistId, isWeek, salonId }, this.props).push(
      !dayView && stylistId && isWeek ? linkTo({ pattern: LINK.checkoutFinishWeek, params: { salonId, stylistId } }) : linkTo({ pattern: LINK.checkoutFinishDay, params: { salonId } }),
    );
  };

  removeClient = (index) => void this.props.removeClient(index);

  showTipsRow = () => void this.setState({ tipsRow: true });

  render() {
    const { defaultPayMethod, errorMessageModal, searchDepositAdded, depositLoadedFromServer, tipsRow } = this.state;

    const {
      clientsDeposits,
      products,
      salonId,
      calendarMode,
      clientDepositData,
      checkoutLoaded,
      data,
      t,
      clientId,
      depositUnlock,
      setCheckoutPayButtons,
      fetchAllDepositClientsList,
      stylists,
      updateDepositFromCheckout,
      services,
      checkoutPayButtonsDisabled,
      appointments,
      appointmentStartTimeList,
      fetchNextAppointmentDetails,
      selectedAppointment,
      allProceduresForTheDay,
      cleanCheckoutData,
      errorMessage,
      getAppointmentStartTimeList,
      isFetching,
      chargeCardOnFile,
      clients: clientsFromProps,
      rsCheckoutFetchAllDeposits,
      rsCheckoutFetchOneDeposit,
      rsFetchDepositClientList,
      isSalonInUS,
    } = this.props;

    const { active_card: activeCard, balanceToPay, clients: clientsFromData, depositListData, otherAmount, total } = data ?? {};

    const addedProducts = asArray(clientsFromData)?.flatMap((client) => client?.products ?? []);

    const { totalTaxesAndFeesAmount, fees, taxes } = calculateCheckoutTaxesAndFees({
      clients: clientsFromData ?? [],
      services: this.props.services ?? [],
      products: addedProducts ?? [],
      taxes: this.props.taxes ?? [],
      fees: this.props.fees ?? [],
    });

    const clients = asArray(clientsFromData).map((c, index) => {
      for (let index = 0; index < clientsFromProps.length; index += 1) {
        const client = clientsFromProps[index];

        const clientId = client.id;
        if (clientId !== c?.id) continue;

        return {
          client,
          clientId,
          addedServices: c?.services || [],
          addedProducts: c?.products || [],
          nextAppointment: c?.nextAppointment || null,
          key: `${index}${clientId || 'walkin'}`,
        };
      } // for index

      return {
        client: { user_name: t('checkout.walkIn') },
        clientId: 0,
        addedServices: c?.services,
        addedProducts: c?.products,
        key: index.toString(),
        nextAppointment: null,
      };
    });

    const filterAllClientsDepositList = clientsDeposits.filter(({ clientId: a }) => !depositListData.find(({ clientId: b }) => a === b));

    const searchClients = clientsFromProps
      .filter(({ id: a }) => !clientsFromData.some(({ id: b }) => a === b))
      .map((client) =>
        allProceduresForTheDay.some(({ appointment: { client: c } }) => (c ? c.id === client.id : false)) ? { ...client, has_appointment_today: true } : { ...client, has_appointment_today: false },
      );

    const isLightMode = calendarMode === 'light';
    const isDarkMode = calendarMode === 'dark';
    const isBrightMode = !isLightMode && !isDarkMode;

    const hasClients = clients.length > 0;
    const firstOfClients = first(clients);

    const balanceToPayInclTaxes = balanceToPay + totalTaxesAndFeesAmount;
    const totalInclTaxes = total + totalTaxesAndFeesAmount;
    const checkoutBalance = isSalonInUS ? balanceToPayInclTaxes : balanceToPay;

    const busyComplete = rsCheckoutFetchAllDeposits || rsCheckoutFetchOneDeposit || rsFetchDepositClientList;

    return (
      <div
        data-bem="Checkout"
        className={cn({
          'checkout-wrap': true,
          'light-class': calendarMode === 'light',
          'dark-class': calendarMode === 'dark',
          'bright-class': isBrightMode,
        })}
      >
        {fromAnyToBool(activeCard && defaultPayMethod === '' && firstOfClients) && <SelectPaymentOptionModal client={firstOfClients?.client} onClosePaymentModal={this.onClosePaymentModal} />}

        <CheckoutHeader salonId={salonId} onGoBack={cleanCheckoutData} />

        {fromAnyToBool(errorMessageModal) && <AlertDialog title={errorMessage} buttonText={t('common.buttons.close')} />}

        {fromAnyToBool(isFetching) && (
          <div data-bem="Checkout__loading" className="loading-wrap">
            <div data-bem="Checkout__loading-animation" className="loading-anim">
              <div data-bem="Checkout__loading-title" className="loading-title">
                <span>{t('composite.loadingModal.loading')}</span>
              </div>
            </div>
            <div data-bem="Checkout__loading-message" className="loading-feedback">
              {t('composite.loadingModal.gettingData')}
            </div>
          </div>
        )}

        <div data-bem="Checkout__container" className="checkout-container">
          <CheckoutTitle
            btnText={hasClients ? t('checkout.checkoutTitle.addAnotherClient') : t('checkout.checkoutTitle.addClient')}
            clients={searchClients}
            onAddHandler={(client) => this.addClient(client)}
            mobileClass={cn({
              'move-up': true,
              'no-clients': !hasClients,
              remove: hasClients,
            })}
          />

          {asArray(clients).map(({ addedProducts, addedServices, client, key, nextAppointment }, index) => (
            <CheckoutClient
              key={key}
              salonId={salonId}
              index={index}
              client={client}
              services={services}
              products={products}
              addedServices={addedServices}
              addedProducts={addedProducts}
              nextAppointment={nextAppointment}
              removeClient={this.removeClient}
              stylists={stylists}
              showTip={this.showTipsRow}
              tipBtnVisible={!tipsRow}
              fetchNextAppointmentDetails={fetchNextAppointmentDetails}
              bookingNotes={selectedAppointment && selectedAppointment.notes}
            />
          ))}

          {hasClients && <CheckoutTaxes taxes={taxes} fees={fees} />}

          {fromAnyToBool(tipsRow && hasClients) && <CheckoutTipsContainer />}

          {hasClients && (
            <>
              <div data-bem="Checkout__total" className="toolbar-total-bill">
                {isSalonInUS && (
                  <>
                    <span>
                      {t('checkout.subtotal')} <CurrencySign value={total} />
                    </span>
                    <span>
                      {t('checkout.taxesAndFees')} <CurrencySign value={totalTaxesAndFeesAmount} />
                    </span>
                  </>
                )}
                <span>
                  {t('checkout.totalBill')} <CurrencySign value={isSalonInUS ? totalInclTaxes : total} />{' '}
                </span>
              </div>
            </>
          )}

          {
            // BEGIN of problematic block

            // Produces the following error message in the browser console:
            // > Warning: Cannot update during an existing state transition (such as within `render`).
            // > Render methods should be a pure function of props and state.

            // FIXME: do not cause side effects inside render, nor tucked between tags
            fromAnyToBool(selectedAppointment && !depositLoadedFromServer && checkoutLoaded) && this.setUpDepositFromBackEnd()

            // END of problematic block
          }

          <div className={hasClients ? null : 'push-content-down'} />
          {clientId ? (
            <div>
              <ClientsDepositTable
                depositListData={depositListData}
                checkoutLoaded={checkoutLoaded}
                total={checkoutBalance}
                clients={clientsFromData}
                updateDepositFromCheckout={updateDepositFromCheckout}
                setCheckoutPayButtons={setCheckoutPayButtons}
                appointments={appointments}
                clientId={clientId}
                salonId={salonId}
                clientDepositData={clientDepositData}
                appointmentStartTimeList={appointmentStartTimeList}
                depositUnlock={depositUnlock}
                searchDepositAdded={searchDepositAdded}
              />

              <CheckoutDepositSearch
                clients={clientsFromProps}
                clientsDeposits={filterAllClientsDepositList}
                fetchAllDepositClientsList={fetchAllDepositClientsList}
                onSelectClientDeposit={this.addClientDeposits}
                salonId={salonId}
                getAppointmentStartTimeList={getAppointmentStartTimeList}
              />
            </div>
          ) : fromAnyToBool(isFetching) ? (
            <OverlaySpinner />
          ) : (
            <WalkInInstructions />
          )}

          <CheckoutVoucherV2 clientId={clientId} total={checkoutBalance} />
        </div>

        <CheckoutFooter
          card={activeCard}
          balance={checkoutBalance}
          checkoutOption={defaultPayMethod}
          blockComplete={checkoutPayButtonsDisabled}
          busyComplete={busyComplete}
          otherAmount={otherAmount ? parsfixed(otherAmount) : DEFAULT_OTHER_AMOUNT}
          onComplete={this.handleComplete}
          chargeCardOnFile={chargeCardOnFile}
        />
      </div>
    );
  }
}

const CheckoutHoc = withRouter(
  connect(
    (state) => ({
      salonId: state.currentSalon.id,
      appointments: asObject(state.checkout).appointments,
      clients: state.currentSalon.clientList,
      clientsOutstandingDeposits: asObject(state.checkout).data.deposits.clientsOutstandingDeposits || {},
      clientsDepositsSelected: asObject(state.checkout).data.deposits.clientsDepositsSelected,
      stylists: state.currentSalon.stylistList,
      categories: state.currentSalon.services,
      services: asArray(state.currentSalon.services).reduce((registered, { services }) => registered.concat(services), []),
      products: state.product.response || [],
      data: selectCheckoutData(state),
      isFetching: asObject(state.checkout).isFetching,
      errorMessage: asObject(state.checkout).errorMessage,
      selectedAppointment: asObject(state.checkout).selectedAppointment,
      nextAppointment: asObject(state.checkout).nextAppointment,
      initialDeposit: asObject(state.checkout).initialDeposit,
      depositListData: asObject(state.checkout).depositListData,
      checkoutPayButtonsDisabled: asObject(state.checkout).checkoutPayButtonsDisabled,
      clientId: asObject(state.checkout).clientId,
      allProceduresForTheDay: asObject(state.checkout).allProceduresForTheDay,
      checkoutLoaded: asObject(state.checkout).checkoutLoaded,
      chargeCardOnFile: selectOldFlagsChargeCardOnFile(state),
      clientsDeposits: asObject(state.checkout).clientsDeposits,
      clientDepositData: state.booking.clientDepositData,
      appointmentStartTimeList: state.booking.appointmentStartTimeList,
      depositUnlock: asObject(state.checkout).depositUnlock,
      calendarMode: state.currentSalon.calendar_mode,
      rsCheckoutFetchAllDeposits: selectRsCheckoutFetchAllDeposits(state),
      rsCheckoutFetchOneDeposit: selectRsCheckoutFetchOneDeposit(state),
      rsFetchDepositClientList: selectRsFetchDepositClientList(state),
    }),
    mapActionsToDispatchToProps({
      setTotal: setCheckoutTotal,
      addClient,
      addClientDeposits,
      addClientSearchDeposits,
      removeClient,
      fetchProductList,
      getClientsList,
      getCategories,
      fetchStylistsBySalon,
      fetchDeposit,
      fetchClientDepositList,
      cleanCheckoutData: feCleanCheckoutData,
      startCheckoutSession,
      closeCheckoutSession,
      updateCheckoutData,
      updateDepositFromCheckout,
      setCheckoutPayButtons,
      fetchAllDepositClientsList,
      fetchNextAppointmentDetails,
      getAppointmentStartTimeList,
    }),
  )(withTranslation()(withGilt(Checkout))),
);

export default CheckoutHoc;
